import { useEffect, useState } from "react";
import { Plus, X, Heart, Route as RouteIcon } from "lucide-react";
import {
  loadSavedPlaces,
  iconFor,
  type SavedPlace,
} from "@/utils/savedPlaces";
import { loadHeartedPlaces, type HeartedPlace } from "@/utils/heartedPlaces";
import { loadSavedTrips, type SavedTrip } from "@/utils/savedTrips";
import { type ParkedSpot } from "@/utils/parkedCar";

interface Props {
  /** Tap a place chip → fill the active field with this place. */
  onPick: (place: SavedPlace) => void;
  /** Tap a hearted-place chip → fill the active field with the hearted spot. */
  onPickHearted?: (place: HeartedPlace) => void;
  /** Tap a saved-trip chip → load the entire trip (start + end + stops). */
  onPickTrip?: (trip: SavedTrip) => void;
  /** Tap "Edit" then a labeled chip → rename/delete via the dialog. */
  onEdit?: (place: SavedPlace) => void;
  /** Tap "Edit" then a saved-trip chip → delete it. */
  onDeleteTrip?: (trip: SavedTrip) => void;
  /** Tap the "+ Label" button → open the create dialog (host decides target). */
  onAdd?: () => void;
}

function heartedLabel(h: HeartedPlace): string {
  if (h.note?.trim()) return h.note.trim();
  return `${h.coords[1].toFixed(3)}, ${h.coords[0].toFixed(3)}`;
}

/**
 * Horizontal scroll-row of saved things you can drop into a search field:
 * labeled places (Home/Work/…), hearted places (private favorites), and
 * saved trips (load the whole start+end+stops at once).
 *
 * Tap = use. Tap "Edit" → labeled chips become rename/delete and saved-trip
 * chips become delete.
 */
export const SavedPlacesChips = ({
  onPick,
  onPickHearted,
  onPickTrip,
  onEdit,
  onDeleteTrip,
  onAdd,
}: Props) => {
  const [places, setPlaces] = useState<SavedPlace[]>(() => loadSavedPlaces());
  const [hearted, setHearted] = useState<HeartedPlace[]>(() => loadHeartedPlaces());
  const [trips, setTrips] = useState<SavedTrip[]>(() => loadSavedTrips());
  // Parked-car feature removed Aug 7; state stays null forever but keeps
  // the nothingToShow logic shape unchanged.
  const [parked] = useState<ParkedSpot | null>(null);
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    const refresh = () => {
      setPlaces(loadSavedPlaces());
      setHearted(loadHeartedPlaces());
      setTrips(loadSavedTrips());
    };
    window.addEventListener("saved-places-changed", refresh);
    window.addEventListener("hearted-places-changed", refresh);
    window.addEventListener("saved-trips-changed", refresh);
    window.addEventListener("parked-car-changed", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("saved-places-changed", refresh);
      window.removeEventListener("hearted-places-changed", refresh);
      window.removeEventListener("saved-trips-changed", refresh);
      window.removeEventListener("parked-car-changed", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  const nothingToShow =
    places.length === 0 && hearted.length === 0 && trips.length === 0 && parked == null;
  if (nothingToShow) return null;

  const baseChip =
    "h-9 px-3 rounded-full bg-muted/60 border border-muted-foreground/20 text-xs font-medium flex items-center gap-1.5 text-muted-foreground hover:bg-muted hover:text-foreground transition-smooth flex-shrink-0";

  return (
    <div className="mt-2 flex items-center gap-2">
      <div className="flex-1 flex items-center gap-2 overflow-x-auto no-scrollbar pb-0.5">

        {/* (Parked-car chip removed — Morgan, Aug 7: "just get rid of
            this", after the stale-fix wrong-pin episode in Umbria.) */}

        {/* Saved trips first — highest signal: one tap reloads a whole route */}
        {trips.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => (editing ? onDeleteTrip?.(t) : onPickTrip?.(t))}
            className={
              baseChip +
              " border-primary/30 text-foreground bg-primary/5 hover:bg-primary/10 " +
              (editing ? "border-dashed border-destructive/60 text-destructive" : "")
            }
            aria-label={editing ? `Delete trip ${t.label}` : `Load trip ${t.label}`}
            title={`${t.start.place} → ${t.end.place}`}
          >
            <RouteIcon className="w-3.5 h-3.5" />
            <span className="max-w-[10rem] truncate">{t.label}</span>
            {editing && <X className="w-3 h-3 ml-0.5 opacity-60" />}
          </button>
        ))}

        {/* Labeled places (Home/Work/…) */}
        {places.map((p) => (
          <button
            key={p.id}
            type="button"
            onClick={() => (editing ? onEdit?.(p) : onPick(p))}
            className={baseChip + (editing ? " border-dashed border-foreground/40" : "")}
            aria-label={editing ? `Edit ${p.label}` : `Use ${p.label}`}
          >
            <span aria-hidden>{p.icon ?? iconFor(p.label)}</span>
            <span className="max-w-[10rem] truncate">{p.label}</span>
            {editing && <X className="w-3 h-3 ml-0.5 opacity-60" />}
          </button>
        ))}

        {/* Hearted places — private favorites; only pickable, not editable here */}
        {!editing && hearted.map((h) => (
          <button
            key={h.id}
            type="button"
            onClick={() => onPickHearted?.(h)}
            className={baseChip}
            aria-label={`Use hearted spot ${heartedLabel(h)}`}
          >
            <Heart className="w-3.5 h-3.5 fill-current text-rose-500" />
            <span className="max-w-[10rem] truncate">{heartedLabel(h)}</span>
          </button>
        ))}
      </div>

      {(places.length > 0 || trips.length > 0) && (
        <button
          type="button"
          onClick={() => setEditing((v) => !v)}
          className="h-9 px-2.5 rounded-full text-[11px] text-muted-foreground hover:text-foreground transition-smooth flex-shrink-0"
        >
          {editing ? "Done" : "Edit"}
        </button>
      )}
    </div>
  );
};
